/**
 * Unified official-path resolver for lesson/module "Continue" CTAs.
 *
 * Priority order (highest first):
 *   1. Adaptive Learning Engine — when a passed-in engine decision provides
 *      a safe `target_route` (not locked, not error) we honor it.
 *   2. Beta path / roster — when the learner is in the beta path, walk the
 *      curated Launch Path items that were loaded from Supabase.
 *   3. Curriculum-order fallback — for non-beta learners, ask Supabase for
 *      the next lesson in the current course by `lesson_order`.
 *   4. Dashboard fallback — `/dashboard` whenever the resolver cannot
 *      confidently determine the next lesson.
 *
 * This resolver is ROUTING ONLY. It must never:
 *   - Mutate completion, rewards, retake, mastery, or telemetry state.
 *   - Unlock lessons, mark anything complete, or advance an incomplete lesson.
 *   - Contact any RPC that writes.
 *
 * Callers are still responsible for their own pass/completion gating before
 * showing a "Continue" CTA — this file assumes it is only called after the
 * learner has already legitimately completed the current step.
 *
 * Standalone beta lessons
 * -----------------------
 * A lesson may be marked `is_beta_content = true` while intentionally having
 * `beta_path_id = NULL` and `beta_order = NULL`. Those rows are standalone
 * beta-quality / bonus beta lessons that are NOT part of any curated beta
 * roster. The resolver must not try to sequence them into an official beta
 * path — if the current lesson is standalone-beta, the beta branch (2) will
 * simply not find a "next" item and the resolver falls through safely to the
 * curriculum-order or dashboard fallback.
 *
 * Product should only assign such a lesson to a beta path (`beta_path_id`
 * + `beta_order`) by explicit decision. Known standalone beta lesson today:
 *   - "Borrowing Money" — lesson_id db6fdc3b-f16d-4fe5-a750-c65c2c8dc5a1
 *     (course: Credit & Debt Management)
 */
import { supabase } from "@/integrations/supabase/client";
import { getNextLaunchRouteAfterLesson, type LaunchProgressionItem } from "@/lib/launch/launchProgression";
import type { LearningEngineDecision } from "@/lib/learning-engine/types";
import type { AnyRow } from "@/lib/rowTypes";

export const DASHBOARD_ROUTE = "/dashboard";

export interface NextLessonResolverInput {
  /** Learner access tier. Only `"beta"` follows the beta roster. */
  accessTier?: string | null;
  /** Lesson the learner just completed (UUID). */
  currentLessonId?: string | null;
  /** Course name for the current lesson — used only for the fallback path. */
  courseName?: string | null;
  /** Loaded Launch Path items (order-sensitive) — required for beta learners. */
  betaLaunchItems?: readonly LaunchProgressionItem[];
  /** Optional Adaptive Learning Engine decision, if already available. */
  engineDecision?: LearningEngineDecision | null;
}

export interface NextLessonResolution {
  route: string;
  /** Which tier of the priority order produced the route. */
  source: "engine" | "beta_path" | "course_order" | "dashboard_fallback";
}

function engineRoute(decision: LearningEngineDecision | null | undefined): string | null {
  if (!decision) return null;
  if (decision.is_locked) return null;
  if (decision.action === "error_safe_locked" || decision.action === "locked") return null;
  const route = decision.target_route;
  if (!route || typeof route !== "string") return null;
  // The engine may hand back `/dashboard` as a safe redirect — treat that as
  // an explicit engine decision, not as a fallback.
  return route;
}

/**
 * Synchronous portion of the resolver — covers engine + beta path. Returns
 * `null` when no synchronous decision is available and the caller should
 * fall through to `resolveCourseOrderFallback`.
 */
export function resolveNextLessonRouteSync(
  input: NextLessonResolverInput,
): NextLessonResolution | null {
  const eRoute = engineRoute(input.engineDecision);
  if (eRoute) return { route: eRoute, source: "engine" };

  if (input.accessTier === "beta") {
    const betaRoute = input.betaLaunchItems && input.currentLessonId
      ? getNextLaunchRouteAfterLesson(input.betaLaunchItems, input.currentLessonId)
      : null;
    if (betaRoute) return { route: betaRoute, source: "beta_path" };
    // Beta learner with no valid next beta item → safe dashboard fallback.
    return { route: DASHBOARD_ROUTE, source: "dashboard_fallback" };
  }

  return null;
}

/**
 * Non-beta curriculum-order fallback. READ-ONLY. Looks up the next lesson
 * in the same course by `lesson_order`; falls back to `/dashboard` when no
 * next lesson exists or the lookup fails.
 */
export async function resolveCourseOrderFallback(input: {
  courseName?: string | null;
  currentLessonId?: string | null;
}): Promise<NextLessonResolution> {
  if (!input.courseName || !input.currentLessonId) {
    return { route: DASHBOARD_ROUTE, source: "dashboard_fallback" };
  }
  try {
    const { data, error } = await supabase
      .from("lessons")
      .select("lesson_id, lesson_order")
      .eq("course_name", input.courseName)
      .order("lesson_order");
    if (error || !data) {
      return { route: DASHBOARD_ROUTE, source: "dashboard_fallback" };
    }
    const current = data.find((l: AnyRow) => l.lesson_id === input.currentLessonId);
    if (!current) {
      return { route: DASHBOARD_ROUTE, source: "dashboard_fallback" };
    }
    const laterLessons = data
      .filter((l: AnyRow) => l.lesson_order > current.lesson_order)
      .sort((a: AnyRow, b: AnyRow) => a.lesson_order - b.lesson_order);
    if (laterLessons.length > 0) {
      // Only offer a lesson the learner is actually allowed to open: the
      // release gate is the same contract the lesson route enforces. This
      // keeps Continue working across every course, not just the launch path.
      const { data: released } = await supabase
        .from("lesson_release_status")
        .select("lesson_id")
        .in("lesson_id", laterLessons.map((l: AnyRow) => l.lesson_id))
        .eq("release_ready", true);
      const readyIds = new Set((released ?? []).map((r: AnyRow) => r.lesson_id));
      const next = laterLessons.find((l: AnyRow) => readyIds.has(l.lesson_id));
      if (next) {
        return { route: `/lesson/${next.lesson_id}`, source: "course_order" };
      }
    }
    return { route: DASHBOARD_ROUTE, source: "dashboard_fallback" };
  } catch {
    return { route: DASHBOARD_ROUTE, source: "dashboard_fallback" };
  }
}

/**
 * Full unified resolver. Runs the synchronous tiers first; only touches the
 * network for the non-beta course-order fallback.
 */
export async function resolveNextLessonRoute(
  input: NextLessonResolverInput,
): Promise<NextLessonResolution> {
  const sync = resolveNextLessonRouteSync(input);
  if (sync) return sync;
  return resolveCourseOrderFallback({
    courseName: input.courseName,
    currentLessonId: input.currentLessonId,
  });
}